import { HttpBackend, HttpClient, HttpHeaders } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { NavigationExtras, Router } from "@angular/router";
import { Observable, Subject, Subscription, of, take, timer } from "rxjs";
import { Base } from "../base";
import { NavigationType, ProductName } from "../definitions/base.definitions";
import { DialogManager } from "./dialog.manager";
import { DrawerManager } from "./drawer.manager";
import { Environment } from "../definitions/environment.definitions";
import { Location } from "@angular/common";


@Injectable({
    providedIn: 'root',
})
export class RouterManager {

    readonly DrawerManager = inject(DrawerManager);
    readonly DialogManager = inject(DialogManager);
    readonly Router = inject(Router);
    readonly HttpBackend = inject(HttpBackend);

    private readonly errorRefreshStorageKey = '_refreshOnError';

    private _navigating: Subject<void> = new Subject();
    private _breadCrumbs: string[] = [];
    private _httpClient!: HttpClient;
    private _appRefreshSubscription!: Subscription;
    private _updateAvailable: boolean = false;
    private _lastVersionCheck: number = 0;

    constructor(
        private _Environment: Environment,
        private _Location: Location
    ) {
        this._httpClient = new HttpClient(this.HttpBackend);
        this._lastVersionCheck = new Date().getTime() / 1000;
        this.RefreshApplication();
    }

    Navigate(url: string, extras?: NavigationExtras, navigationType: NavigationType = NavigationType.SameTab, params: string[] = []): Observable<void> {
        switch (navigationType) {
            case NavigationType.NewTab:
                this.NavigateToNewTab(url, extras);
                return of(undefined);
            case NavigationType.UpdateQueryParams:
                this.AddNavigationExtras(extras || {});
                return of(undefined);
            case NavigationType.SameTab:
                return this.DoNavigation(url, extras);
            case NavigationType.RemoveQueryParams:
                this.NavigateAndRemoveQueryParams(params);
                return of(undefined);
        }    
    }

    RefreshedFromErrorDialog() {
        if (localStorage.getItem(this.errorRefreshStorageKey)) {
            this._updateAvailable = true;  // Force refresh on navigation
            this.DoNavigation('home');
        } else {
            localStorage.setItem(this.errorRefreshStorageKey, 'true');
            window.location.reload();
        }
    }

    IsNavigatingToSamePage(route: string): boolean {
        return this.Router.url.includes(route);
    }

    private NavigateAndRemoveQueryParams(params: string[] = []) {
        // Strip the param synchronously so the queryParams observable doesn't re-emit twice.
        // Use Location.path() (base-href stripped) so Location.go() doesn't re-prepend the deployment base href (e.g. /Teacher/v2/en).
        let cleanUrl = new URL(this._Location.path(true), window.location.origin);
        params.forEach(param => {
            cleanUrl.searchParams.delete(param);
        });
        this._Location.go(cleanUrl.pathname + cleanUrl.search + cleanUrl.hash);
    }

    private NavigateToNewTab(path: string, extras?: NavigationExtras) {
        const url = this.CreateFullUrl(path, extras);
        window.open(url, '_blank');
    }

    private AddNavigationExtras(extras: NavigationExtras) {
        this.Router.navigate([], extras);
    }

    private DoNavigation(url: string, extras?: NavigationExtras): Observable<void> {
        const navigationDoneSubject = new Subject<void>();
        localStorage.removeItem(this.errorRefreshStorageKey);
        this._breadCrumbs.push(url);

        this.GetReadyForNavigation(this.IsNavigatingToSamePage(url)).pipe(take(1)).subscribe(() => {
            if (this._updateAvailable) {
                window.location.href = this.CreateFullUrl(url, extras);
                navigationDoneSubject.next();
            } else {
                this.Router.navigate([url], extras).then(() => {
                    // TODO: Fix once route animations are done better
                    timer(300).subscribe(() => navigationDoneSubject.next());
                });
            }
        });

        return navigationDoneSubject.pipe(take(1));
    }

    private GetReadyForNavigation(isSamePage: boolean): Observable<void> {
        this.DialogManager.DismissDialog();
        this.DrawerManager.CloseAllDrawers(true);

        // Staying on the same page (e.g. opening a drawer via query params) isn't a real page
        // transition, so don't cancel the current page's own in-flight/queued API requests.
        if (!isSamePage) {
            this._navigating.next();
        }

        return this.DrawerManager.allDrawersClosed;
    }

    private IsNewApplicationAvailable(): void {
        this._httpClient.head(window.location.href, { observe: 'response', headers: new HttpHeaders({ 'Cache-Control': 'no-cache' })}).subscribe({ 
            next: (response) => {
                const serverVersion = response.headers.get('Last-Modified');
                const appVersion = document.lastModified;
                const serverVersionDate = serverVersion ? new Date(serverVersion).getTime() : null;
                const appVersionDate = new Date(appVersion).getTime();
                const isServerVersionOlderThan5Minutes = serverVersionDate ? (new Date().getTime() - serverVersionDate) > 5 * 60 * 1000 : false;
                if (serverVersionDate && serverVersionDate !== appVersionDate && isServerVersionOlderThan5Minutes) {
                    this._updateAvailable = true;
                }
                this._lastVersionCheck =  new Date().getTime() / 1000;
                this.RefreshApplication();
            },
            error: () => {
                this._lastVersionCheck =  new Date().getTime() / 1000;
                this.RefreshApplication();
            }
        });
    }

    private RefreshApplication() {
        const currentTime = Math.round(new Date().getTime() / 1000);
        const delay = Math.max(0, (10 * 60) - (currentTime - this._lastVersionCheck));   // Adjust based on how far we are from last update time
        this._appRefreshSubscription?.unsubscribe();
        this._appRefreshSubscription = timer(delay * 1000).subscribe(() => this.IsNewApplicationAvailable());
    }

    CreateFullUrl(path: string, extras?: NavigationExtras): string {
        const baseSegments = this.GetBaseSegments(path);
        const urlTree = this.Router.createUrlTree(baseSegments, {
            queryParams: extras?.queryParams,
            fragment: extras?.fragment,
        });

        return this.Router.serializeUrl(urlTree).replace(/%2F/g, '/');
    }

    /** Get base URL segments for routing, accounting for environment and current context */
    GetBaseSegments(path: string): string[] {
        const currentUrl = window.location.pathname;
        let currentSegments = currentUrl.split('/').filter(segment => segment);
        currentSegments = currentSegments.slice(0, 3);
        return this._Environment.Application.ProductName === ProductName.Local ? [path] : [...currentSegments, path];
    }

    /** Check if a candidate route matches the given route path (exact or descendant match) */
    IsRouteMatch(candidate: string, route: string): boolean {
        const path = candidate.split(/[?#]/)[0];
        return path === route || path.startsWith(route + '/');
    }

    /** Parse query params and fragment from a URL string */
    ParseQueryParamsAndFragment(url: string): { queryParams?: Record<string, string>; fragment?: string } {
        const [pathAndQuery, fragment] = url.split('#');
        const [, queryString] = pathAndQuery.split(/\?(.*)/s);

        if (!queryString) {
            return { fragment };
        }

        const queryParams = Object.fromEntries(queryString.split('&').map(pair => {
            const [key, value] = pair.split('=');
            return [decodeURIComponent(key), decodeURIComponent(value || '')];
        }));

        return { queryParams, fragment };
    }

    /** Check if a given route is currently active in the router */
    IsRouteActive(route: string, matchType: 'exact' | 'subset' = 'subset'): boolean {
        if (!route) {
            return false;
        }
        return this.Router.isActive(route, { paths: matchType, queryParams: 'ignored', fragment: 'ignored', matrixParams: 'ignored' });
    }

    get navigating(): Observable<void> {
        return this._navigating.asObservable();
    }
}